/**
 * The cohort table (REQ-021 AC-004, AC-005).
 *
 * A table and not a chart, deliberately. Half of what a reader needs here is
 * prose that no mark on an axis can carry: which rows rest on eleven people,
 * which cell is missing and why, and — above all — what the last column is
 * not. A cohort line chart would draw the net paying ratio as a curve indexed
 * from 100%, which is the exact picture of retention this measure is not, and
 * it would have to drop the rows that have no figure rather than let them say
 * so. Twelve rows and three measures fit on a screen; the honesty does not fit
 * on a chart.
 *
 * Retention and net paying count sit side by side because their difference is
 * the finding, and a reader meeting 40% beside 1.00× has to be able to see why
 * both are true — the cohort lost three fifths of the accounts it was paying
 * with, and converted as many new members in their place. So the two columns
 * are drawn in the same weight, headed with the question each answers, and
 * explained together above the table. Neither is presented as the corrected
 * version of the other.
 *
 * The column that needed the most care is still the net paying one.
 * `backend/src/services/dashboard/cohorts.ts` went to some length to stop it
 * being read as retention — it is a ratio of two *counts* of a cohort's payers
 * taken on two days, so a cohort that lost every payer it had and converted the
 * same number of new ones reports 100%, and a growing cohort reports more. This
 * screen therefore never uses the word for it, never renders it as a
 * percentage with a tick beside it, never colours it by size, and never offers
 * to sort by it: the rows stay in calendar order, because a ranking by that
 * column would be a claim that a larger figure is a better one.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Layers, Loader2, Minus } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import {
  cohortLabel,
  COHORT_CONVERSION_ID,
  COHORT_NET_PAYING_ID,
  COHORT_RETENTION_ID,
  discardedAccounts,
  measureDefinition,
  rowProvisional,
  type CohortMeasure,
  type CohortsMeta,
  type CohortsPayload,
  type NetPayingMeasure,
  type RetentionMeasure,
} from "./cohorts";
import { formatValue, type MetricUnit } from "./metrics";

const badge =
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium";

/**
 * One measured cell, or the sentence that says why there is none.
 *
 * Both figures are drawn in the same weight and the same colour. A green cell
 * would rank the cohorts for the reader, and on the net paying column a green
 * 100% is precisely the misreading the backend refused to ship.
 */
function MeasureCell({
  measure,
  unit,
  provisionalBelow,
  denominatorLabel,
  testId,
  children,
}: {
  measure: CohortMeasure;
  unit: MetricUnit;
  provisionalBelow?: number;
  denominatorLabel: (denominator: number) => string;
  testId: string;
  children?: ReactNode;
}) {
  return (
    <td className="px-4 py-3 align-top" data-testid={testId}>
      {measure.value.status === "ok" ? (
        <>
          <span className="text-sm font-semibold tabular-nums text-gray-900">
            {formatValue(measure.value.value, unit)}
          </span>
          <span className="mt-0.5 block text-xs text-gray-500">
            {denominatorLabel(measure.denominator)}
          </span>
        </>
      ) : (
        <>
          {/* Never a dash on its own, never a zero, never an empty cell: the
              reason the payload gives is the cell. */}
          <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-800">
            <Minus className="h-3.5 w-3.5" aria-hidden="true" /> Not measured
          </span>
          <span className="mt-0.5 block text-xs text-amber-900">
            {measure.reason ??
              "The payload gave no reason, which is itself a defect."}
          </span>
        </>
      )}

      {measure.provisional && provisionalBelow !== undefined && (
        <span className="mt-1 block text-xs text-amber-800">
          Provisional: computed on {measure.denominator} subjects, below the{" "}
          {provisionalBelow} this measure needs (AC-005).
        </span>
      )}

      {children}
    </td>
  );
}

/**
 * The observation window, on every net paying cell that has one.
 *
 * A figure observed from a date is not a figure observed since signup, and the
 * difference is the whole reason the column is honest: the store began keeping
 * cohort counts on a particular day, and everything before that day is
 * unobserved rather than unchanged.
 */
/**
 * The day retention's roster was taken, on every cell that has one.
 *
 * Said in different words from the net paying window on purpose: that one names
 * the day the counting began, this one names the day a fixed set of accounts was
 * written down. Two cells reading "observed from" would invite the reader to
 * take them for the same kind of measurement.
 */
function BaselineDay({ measure }: { measure: RetentionMeasure }) {
  if (!measure.baselineDate) return null;
  return (
    <span className="mt-1 block text-xs text-gray-500">
      The accounts paying on {measure.baselineDate}
      {measure.observedTo ? `, followed to ${measure.observedTo}` : ""}.
    </span>
  );
}

function ObservationWindow({ measure }: { measure: NetPayingMeasure }) {
  if (!measure.observedFrom) return null;
  return (
    <span className="mt-1 block text-xs text-gray-500">
      Observed from {measure.observedFrom}
      {measure.observedTo ? ` to ${measure.observedTo}` : ""}, not from the
      cohort&rsquo;s own month.
    </span>
  );
}

export default function DashboardCohortsPage() {
  const fmt = useLocaleFormat();
  const cohorts = useQuery({
    queryKey: ["dashboard", "cohorts"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: CohortsPayload;
        meta: CohortsMeta;
      }>("/staff/dashboard/cohorts");
      return response.data;
    },
    retry: false,
    refetchInterval: 5 * 60_000,
  });

  // Everything below is read through the error flag, not around it. React-query
  // keeps the previous `data` when a refetch fails, and this page refetches
  // every five minutes without retrying — so a table drawn from `data` alone
  // would go on showing the 10:00 figures, labelled with the 10:00 read, under
  // a banner saying the store could not be read. That is the unreadable store
  // rendering as a complete company, which is the failure this screen exists to
  // prevent, and it is what the sibling executive screen refuses for the same
  // reason.
  const payload = cohorts.isError ? undefined : cohorts.data?.data;
  const meta = cohorts.isError ? undefined : cohorts.data?.meta;
  const conversionDefinition = payload
    ? measureDefinition(payload.definitions, COHORT_CONVERSION_ID)
    : undefined;
  const retentionDefinition = payload
    ? measureDefinition(payload.definitions, COHORT_RETENTION_ID)
    : undefined;
  const netPayingDefinition = payload
    ? measureDefinition(payload.definitions, COHORT_NET_PAYING_ID)
    : undefined;
  const conversionUnit: MetricUnit = conversionDefinition?.unit ?? "rate";
  const retentionUnit: MetricUnit = retentionDefinition?.unit ?? "rate";
  const netPayingUnit: MetricUnit = netPayingDefinition?.unit ?? "ratio";

  // Newest first, and by month only. There is deliberately no control that
  // orders the table by either measure.
  const rows = [...(payload?.cohorts ?? [])].sort((a, b) =>
    b.cohort.localeCompare(a.cohort),
  );
  const storeUnreadable = Boolean(meta) && meta?.dataAsOf === null;
  const discarded = meta ? discardedAccounts(meta) : [];
  // The two are counted apart: a group with no counter cannot be added, and it
  // cannot be left out of the section either, so it says that it is missing.
  const counted = discarded.filter(
    (group): group is Required<typeof group> => group.count !== undefined,
  );
  const uncounted = discarded.filter((group) => group.count === undefined);
  const discardedTotal = counted.reduce((sum, group) => sum + group.count, 0);

  const conversionDenominator = (denominator: number) =>
    `of ${denominator} accounts created`;
  const payingDenominator = (denominator: number) =>
    `of ${denominator} paying at the baseline`;
  const rosterDenominator = (denominator: number) =>
    `of ${denominator} accounts paying at the baseline`;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Layers className="h-5 w-5 text-sky-600" /> Cohorts
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Every account by the month it was created, because an aggregate
            hides whether newer cohorts behave better than older ones. Both
            measures are defined where they are computed —{" "}
            <Link
              to="/admin/dashboard/definitions"
              className="font-medium text-sky-700 hover:text-sky-900"
            >
              see the definitions
            </Link>
            .
          </p>
        </div>
        {meta && (
          <p className="text-xs text-gray-500">
            Read {fmt.dateTime(meta.computedAt)}
          </p>
        )}
      </div>

      {cohorts.isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {cohorts.isError && (
        <div
          data-testid="cohorts-error"
          className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            The cohort table could not be read. No table is drawn below, and no
            row is kept from an earlier read — an empty table would read as a
            company with no signups, and a table of the last figures that loaded
            would read as the current ones.
          </p>
        </div>
      )}

      {/* The two measures a reader will otherwise take for one, side by side.
          It comes before the net paying explainer because the question it
          answers arrives first: why the same row carries two different figures
          about the same month, and why neither replaces the other. */}
      {payload && (
        <section
          data-testid="two-measures-explainer"
          className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
        >
          <h2 className="font-medium">Two measures, two questions</h2>
          <p className="mt-1">
            <span className="font-medium">Retention</span> follows the same
            accounts: those a cohort was paying with on its baseline day, and
            how many of those same accounts still pay. It is a subset of a fixed
            population, so it can never exceed 100%.{" "}
            <span className="font-medium">Net paying count</span> compares two
            totals of the month — how many paid then against how many pay now —
            and the people behind the two are not known to be the same, so it
            can exceed 100%.
          </p>
          <p className="mt-2" data-testid="two-measures-worked-example">
            A cohort reading 40.0% retention beside 1.00× net paying count is
            both at once: three of every five accounts it was paying with have
            gone, and as many other members of that signup month have converted
            since. Neither figure states the other, and neither is the corrected
            version of the other — the gap between them is churn offset by new
            conversions, which is the thing worth seeing.
          </p>
          {retentionDefinition ? (
            <p data-testid="retention-formula" className="mt-2">
              {retentionDefinition.formula}
            </p>
          ) : (
            <p data-testid="retention-formula-missing" className="mt-2">
              The payload carried no definition for the retention column, so
              what the figures in it mean cannot be shown. Until it does, read
              the column as nothing at all rather than as a guess at what it
              used to be.
            </p>
          )}
        </section>
      )}

      {/* The explanation the column heading points at, on the page rather than
          behind a hover: the reader who most needs it is the one who has
          already decided the number means retention. */}
      {payload && (
        <section
          data-testid="net-paying-explainer"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <h2 className="font-medium">
            What the net paying column is, and what it is not
          </h2>
          {/* What the figure is comes from the backend's own formula and is
              never paraphrased here: a paraphrase goes on standing, word for
              word and convincingly, after the computation it describes has
              changed. This page says only the things the formula does not — why
              it is drawn as a multiplier, and that retention is not measured
              anywhere in this system. */}
          <p className="mt-1">
            It is not retention — retention is the column beside it, over a
            different population and answering a different question. It is
            written on this screen as a multiplier rather than a percentage,
            because a percentage invites the reading the figure cannot support.
            The definition is the one the computation carries:
          </p>
          {netPayingDefinition ? (
            <p data-testid="net-paying-formula" className="mt-2">
              {netPayingDefinition.formula}
            </p>
          ) : (
            <p data-testid="net-paying-formula-missing" className="mt-2">
              The payload carried no definition for this column, so what the
              figures in it mean cannot be shown. Until it does, read the column
              as nothing at all rather than as a guess at what it used to be.
            </p>
          )}
        </section>
      )}

      {storeUnreadable && (
        <div
          data-testid="cohorts-store-unreadable"
          className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            The users collection could not be read, so no cohort could be built
            at all. This is not a table with no rows in it, and it is not a
            company with no signups.
          </p>
        </div>
      )}

      {/* The accounts that are in the users collection and in none of the rows.
          They exist because records were thrown away, and a table that hides
          that is claiming a completeness it does not have. */}
      {meta && !storeUnreadable && (
        <section
          data-testid="cohorts-discarded"
          className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700"
        >
          <h2 className="font-medium text-gray-900">
            Accounts the table does not show
          </h2>
          {discardedTotal === 0 && uncounted.length === 0 ? (
            <p className="mt-1 text-gray-600">
              None. Every account carries a creation date inside the window this
              table covers.
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-gray-600">
              {counted
                .filter((group) => group.count > 0)
                .map((group) => (
                  <li key={group.key} data-testid={`discarded-${group.key}`}>
                    <span className="font-medium tabular-nums text-gray-900">
                      {group.count}
                    </span>{" "}
                    {group.label}.
                  </li>
                ))}
              {uncounted.map((group) => (
                <li
                  key={group.key}
                  data-testid={`discarded-uncounted-${group.key}`}
                  className="text-amber-800"
                >
                  The payload sent no count of the accounts that {group.label},
                  so this section is not complete.
                </li>
              ))}
            </ul>
          )}
          {/* Not an account the table discarded but one the users collection no
              longer holds, and the only bias in retention that runs the
              flattering way: a departure erased from the roster takes its own
              churn with it. Shown only when there is one, and worded so it is
              not read as another bucketing rule. */}
          {meta.unattributedAccounts > 0 && (
            <p
              data-testid="cohorts-unattributed"
              className="mt-2 text-amber-800"
            >
              <span className="font-medium tabular-nums">
                {meta.unattributedAccounts}
              </span>{" "}
              accounts the subscription log records changes for are no longer in
              the users collection, so they are in no cohort&rsquo;s roster.
              Retention reads slightly high by however many of them had stopped
              paying.
            </p>
          )}
        </section>
      )}

      {payload && !storeUnreadable && rows.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Signup month</th>
                  <th className="px-4 py-2 font-medium">Registered</th>
                  <th className="px-4 py-2 font-medium">Paying now</th>
                  <th className="px-4 py-2 font-medium">
                    {conversionDefinition?.label ?? "Registration → paid"}
                  </th>
                  <th
                    data-testid="retention-header"
                    className="px-4 py-2 font-medium"
                  >
                    Retention
                    <span className="mt-0.5 block text-[11px] font-semibold normal-case tracking-normal text-sky-800">
                      The same accounts, followed. Never above 100%.
                    </span>
                  </th>
                  <th
                    data-testid="net-paying-header"
                    className="px-4 py-2 font-medium"
                  >
                    Net paying count
                    <span className="mt-0.5 block text-[11px] font-semibold normal-case tracking-normal text-amber-800">
                      A ratio of two counts. Not retention — see above.
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr
                    key={row.cohort}
                    data-testid={`cohort-${row.cohort}`}
                    className="align-top"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-medium text-gray-900">
                        {cohortLabel(row.cohort)}
                      </span>
                      {rowProvisional(row) && (
                        <span
                          className={`mt-1 block w-fit ${badge} bg-amber-100 text-amber-800`}
                        >
                          Provisional
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {row.registered}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {row.payingNow}
                    </td>
                    <MeasureCell
                      measure={row.conversion}
                      unit={conversionUnit}
                      provisionalBelow={conversionDefinition?.provisionalBelow}
                      denominatorLabel={conversionDenominator}
                      testId={`conversion-${row.cohort}`}
                    />
                    <MeasureCell
                      measure={row.retention}
                      unit={retentionUnit}
                      provisionalBelow={retentionDefinition?.provisionalBelow}
                      denominatorLabel={rosterDenominator}
                      testId={`retention-${row.cohort}`}
                    >
                      <BaselineDay measure={row.retention} />
                    </MeasureCell>
                    <MeasureCell
                      measure={row.netPaying}
                      unit={netPayingUnit}
                      provisionalBelow={netPayingDefinition?.provisionalBelow}
                      denominatorLabel={payingDenominator}
                      testId={`net-paying-${row.cohort}`}
                    >
                      <ObservationWindow measure={row.netPaying} />
                    </MeasureCell>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                <tr data-testid="cohort-aggregate" className="align-top">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    All cohorts
                    {rowProvisional(payload.aggregate) && (
                      <span
                        className={`mt-1 block w-fit ${badge} bg-amber-100 text-amber-800`}
                      >
                        Provisional
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-700">
                    {payload.aggregate.registered}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-700">
                    {payload.aggregate.payingNow}
                  </td>
                  <MeasureCell
                    measure={payload.aggregate.conversion}
                    unit={conversionUnit}
                    provisionalBelow={conversionDefinition?.provisionalBelow}
                    denominatorLabel={conversionDenominator}
                    testId="conversion-aggregate"
                  />
                  <MeasureCell
                    measure={payload.aggregate.retention}
                    unit={retentionUnit}
                    provisionalBelow={retentionDefinition?.provisionalBelow}
                    denominatorLabel={rosterDenominator}
                    testId="retention-aggregate"
                  >
                    <BaselineDay measure={payload.aggregate.retention} />
                  </MeasureCell>
                  <MeasureCell
                    measure={payload.aggregate.netPaying}
                    unit={netPayingUnit}
                    provisionalBelow={netPayingDefinition?.provisionalBelow}
                    denominatorLabel={payingDenominator}
                    testId="net-paying-aggregate"
                  >
                    <ObservationWindow measure={payload.aggregate.netPaying} />
                  </MeasureCell>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      {/* An empty table is a statement about the window, not about the company:
          every account could be future-dated or older than the horizon and the
          rows would still be gone. */}
      {payload && !storeUnreadable && rows.length === 0 && (
        <p
          data-testid="cohorts-empty"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          The users collection was read and no signup month falls inside the
          window this table covers. That is a statement about the window, not a
          company with no signups — the accounts outside it are counted above.
        </p>
      )}
    </div>
  );
}
